import React, { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import axios from 'axios';
import './MyDesk.css';

// Import sub-components
import TasksCard from './components/TasksCard';
import CalendarCard from './components/CalendarCard';
import MessagesCard from './components/MessagesCard';
import NotesCard from './components/NotesCard';
import QuickActionsCard from './components/QuickActionsCard';

// Mobile components
import MobileBottomNav from './components/MobileBottomNav';
import MobileDashboard from './components/MobileDashboard';
import MobileTasksView from './components/MobileTasksView';
import MobileCalendarView from './components/MobileCalendarView';
import MobileMessagesView from './components/MobileMessagesView';
import MobileNotesView from './components/MobileNotesView';

const MyDesk = () => {
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileView, setMobileView] = useState('home');

  // Shared state for all modules
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch all data on mount
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const [tasksRes, eventsRes, notesRes, conversationsRes, unreadRes, usersRes] = await Promise.all([
          axios.get(`${API_BASE}/calendar/tasks`, { withCredentials: true }),
          axios.get(`${API_BASE}/calendar/events`, { withCredentials: true }),
          axios.get(`${API_BASE}/calendar/notes/history`, { withCredentials: true }),
          axios.get(`${API_BASE}/messenger/conversations`, { withCredentials: true }),
          axios.get(`${API_BASE}/messenger/unread-count`, { withCredentials: true }),
          axios.get(`${API_BASE}/users/admin-only`, { withCredentials: true })
        ]);

        setTasks(tasksRes.data || []);
        setEvents(eventsRes.data || []);
        setNotes(notesRes.data || []);
        setConversations(conversationsRes.data || []);
        setUnreadCount(unreadRes.data?.unread_count || 0);
        setAdminUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [API_BASE]);

  // Get task stats
  const getTaskStats = () => {
    const userTasks = tasks.filter(task => task.assigned_to === user?.id);
    const assignedCount = userTasks.filter(task => task.status !== 'completed').length;
    const completedCount = userTasks.filter(task => task.status === 'completed').length;
    const overdueCount = userTasks.filter(task => {
      if (task.status === 'completed') return false;
      const taskDate = new Date(task.task_date);
      return taskDate < new Date().setHours(0, 0, 0, 0);
    }).length;
    return { assignedCount, completedCount, overdueCount };
  };

  // Get today's events
  const getTodayEvents = () => {
    const today = new Date().toISOString().split('T')[0];
    return events.filter(event => event.event_date?.substring(0, 10) === today);
  };

  // Format current date
  const formatDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  const taskStats = getTaskStats();
  const todayEvents = getTodayEvents();

  // Mobile view render
  if (isMobile) {
    return (
      <div className="my-desk-mobile">
        {mobileView === 'home' && (
          <MobileDashboard
            user={user}
            taskStats={taskStats}
            todayEvents={todayEvents}
            unreadCount={unreadCount}
            notesCount={notes.length}
            tasks={tasks}
            onNavigate={setMobileView}
          />
        )}
        {mobileView === 'tasks' && (
          <MobileTasksView
            tasks={tasks}
            setTasks={setTasks}
            user={user}
            adminUsers={adminUsers}
            API_BASE={API_BASE}
          />
        )}
        {mobileView === 'calendar' && (
          <MobileCalendarView
            events={events}
            setEvents={setEvents}
            tasks={tasks}
            user={user}
            adminUsers={adminUsers}
            API_BASE={API_BASE}
          />
        )}
        {mobileView === 'messages' && (
          <MobileMessagesView
            conversations={conversations}
            setConversations={setConversations}
            user={user}
            adminUsers={adminUsers}
            API_BASE={API_BASE}
          />
        )}
        {mobileView === 'notes' && (
          <MobileNotesView
            notes={notes}
            setNotes={setNotes}
            user={user}
            API_BASE={API_BASE}
          />
        )}
        <MobileBottomNav
          activeView={mobileView}
          onNavigate={setMobileView}
          unreadCount={unreadCount}
          taskCount={taskStats.assignedCount}
        />
      </div>
    );
  }

  // Desktop view render
  return (
    <div className="my-desk">
      {/* Page Header */}
      <div className="my-desk-header">
        <div className="header-left">
          <div className="page-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <div className="page-title">
            <h1>My Desk</h1>
            <p>Your personal workspace</p>
          </div>
        </div>
        <div className="header-right">
          <span className="current-date">{formatDate()}</span>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="my-desk-content">
        <div className="dashboard-grid">
          {/* Tasks Card */}
          <TasksCard
            tasks={tasks}
            setTasks={setTasks}
            user={user}
            adminUsers={adminUsers}
            taskStats={taskStats}
            API_BASE={API_BASE}
          />

          {/* Calendar Card */}
          <CalendarCard
            events={events}
            setEvents={setEvents}
            tasks={tasks}
            user={user}
            adminUsers={adminUsers}
            API_BASE={API_BASE}
          />

          {/* Notes Card */}
          <NotesCard
            notes={notes}
            setNotes={setNotes}
            user={user}
            API_BASE={API_BASE}
          />

          {/* Messages Card */}
          <MessagesCard
            conversations={conversations}
            setConversations={setConversations}
            unreadCount={unreadCount}
            setUnreadCount={setUnreadCount}
            user={user}
            API_BASE={API_BASE}
          />

          {/* Quick Actions Sidebar */}
          <QuickActionsCard
            tasks={tasks}
            events={events}
            notes={notes}
            user={user}
          />
        </div>
      </div>
    </div>
  );
};

export default MyDesk;
